const KIND = {
    NETWORK: 'network',
    HTTP: 'http',
    TIMEOUT: 'timeout',
    VALIDATION: 'validation',
    UNKNOWN: 'unknown',
};

const VALID_KINDS = new Set(Object.values(KIND));

const generateCorrelationId = () => {
    const bytes = new Uint8Array(6);
    globalThis.crypto.getRandomValues(bytes);
    let suffix = '';
    for (const byte of bytes) {
        suffix += byte.toString(16).padStart(2, '0');
    }
    return `${Date.now().toString(36)}-${suffix}`;
};

const reduceCause = (cause) => {
    if (cause instanceof Error) {
        return { name: cause.name, message: cause.message };
    }
    if (cause && typeof cause === 'object' && 'status' in cause) {
        return { status: cause.status, url: cause.url || null };
    }
    return null;
};

export class AppError extends Error {
    constructor({ kind, message, status, url, cause, payload, correlationId } = {}) {
        super(message || 'AppError');
        Object.setPrototypeOf(this, AppError.prototype);
        this.name = 'AppError';
        this.kind = VALID_KINDS.has(kind) ? kind : KIND.UNKNOWN;
        this.status = typeof status === 'number' ? status : null;
        this.url = typeof url === 'string' ? url : null;
        this.cause = cause || null;
        this.payload = payload ?? null;
        this.correlationId = correlationId || generateCorrelationId();
    }

    toJSON() {
        return {
            name: this.name,
            kind: this.kind,
            message: this.message,
            status: this.status,
            url: this.url,
            payload: this.payload,
            correlationId: this.correlationId,
            cause: reduceCause(this.cause),
        };
    }
}

const messageFromResponse = ({ status, statusText }) =>
    statusText ? `HTTP ${status} ${statusText}` : `HTTP ${status}`;

const fromResponse = (response, ctx) =>
    new AppError({
        kind: KIND.HTTP,
        message: messageFromResponse(response),
        status: response.status,
        url: response.url || ctx?.url || null,
        cause: response,
        payload: ctx?.payload ?? null,
    });

const fromError = (error, ctx) => {
    const isFetchFailure =
        error instanceof TypeError && /fetch/i.test(error.message);
    const isAbort = error.name === 'AbortError' || error.code === 'ABORT_ERR';
    let kind = KIND.UNKNOWN;
    if (isAbort) {
        kind = KIND.TIMEOUT;
    } else if (isFetchFailure) {
        kind = KIND.NETWORK;
    }
    return new AppError({
        kind,
        message: error.message || 'Unknown error',
        cause: error,
        url: ctx?.url ?? null,
    });
};

const fromString = (input, ctx) =>
    new AppError({
        kind: KIND.UNKNOWN,
        message: input,
        url: ctx?.url ?? null,
    });

const fromUnknown = (input, ctx) =>
    new AppError({
        kind: KIND.UNKNOWN,
        message: 'Unknown error',
        cause: (input ?? null) === null ? null : { value: input },
        url: ctx?.url ?? null,
    });

AppError.KIND = KIND;

AppError.from = (input, ctx) => {
    if (input instanceof AppError) {
        return input;
    }
    if (globalThis.Response && input instanceof globalThis.Response) {
        return fromResponse(input, ctx);
    }
    if (input instanceof Error) {
        return fromError(input, ctx);
    }
    if (typeof input === 'string' && input.length > 0) {
        return fromString(input, ctx);
    }
    return fromUnknown(input, ctx);
};
